import { makeAutoObservable } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { ComplianceApi, checkComplianceUrls } from 'entities/Compliance'
import type { TollFreeStepsStore } from 'pages/settings/pages/compliance/pages/tollFree/store/tollFreeStepsStore'
import { ExampleModalContent } from 'pages/settings/pages/compliance/ui/ExampleModalContent/ExampleModalContent'
import {
  sampleMessageMaxTextLength,
  sampleMessageMinTextLength,
  USE_CASE_DESCRIPTION,
  USE_CASE_SAMPLE_MESSAGE,
  useCaseDescExampleMock,
  useCaseDescMaxTextLength,
  useCaseDescMinTextLength,
  useSampleMessageExampleMock,
} from 'pages/settings/pages/compliance/pages/tollFree/pages/UseCase/constants/constants.tsx'

export class UseCaseStore {
  constructor(private _tollFreeStepsStore: TollFreeStepsStore) {
    makeAutoObservable(this)

    this._tollFreeStepsStore.complianceLayerStore.loadComplianceTollFreeFormFields()
  }

  private _useCaseValue: number | null = null
  private _messageVolumeValue: number | null = null
  private _useCaseDesc = ''
  private _sampleMessage = ''
  privacyPolicyUrl = ''
  termsUrl = ''
  errorPrivacyPolicyUrl = ''
  errorTermsUrl = ''

  get tollFreeStepsStore() {
    return this._tollFreeStepsStore
  }

  get useCases() {
    return this._tollFreeStepsStore.complianceLayerStore.tollFreeUseCases
  }

  get messageVolume() {
    return this._tollFreeStepsStore.complianceLayerStore.tollFreeMessageVolume
  }

  get activeUseCase() {
    return this.useCases.find((useCase) => useCase.id === this.useCaseValue)
  }

  get activeUseCaseTitle() {
    return this.activeUseCase?.label
  }

  get activeUseCaseValue() {
    return this.activeUseCase?.id
  }

  get useCaseValue() {
    return this._useCaseValue
  }

  get messageVolumeValue() {
    return this._messageVolumeValue
  }

  get sampleMessage() {
    return this._sampleMessage
  }

  get useCaseDesc() {
    return this._useCaseDesc
  }

  get aiGenerationStore() {
    return this.tollFreeStepsStore.complianceLayerStore.aiGenerationStore
  }

  get isNextDisabled() {
    if (!this.useCaseDesc || !this._sampleMessage) return true

    const ifWithinLimits = (message: string, minLimit: number, maxLimit: number) =>
      message.length >= minLimit && message.length <= maxLimit

    return (
      !this._messageVolumeValue ||
      !this.useCaseValue ||
      !ifWithinLimits(this.useCaseDesc, useCaseDescMinTextLength, useCaseDescMaxTextLength) ||
      !ifWithinLimits(this._sampleMessage, sampleMessageMinTextLength, sampleMessageMaxTextLength)
    )
  }

  setUseCase = (useCase: number) => {
    this._useCaseValue = useCase
  }

  setMessageVolume = (volume: number) => {
    this._messageVolumeValue = volume
  }

  setUseCaseDesc = (useCaseDesc: string) => {
    this._useCaseDesc = useCaseDesc.slice(0, useCaseDescMaxTextLength)
  }

  setSampleMessage = (sampleMessage: string) => {
    this._sampleMessage = sampleMessage.slice(0, sampleMessageMaxTextLength)
  }

  setUseCaseData = ({
    id,
    sampleMessage,
    useCaseDesc,
    monthlyVolume,
    privacyPolicyUrl,
    termsUrl,
  }: {
    id: number
    sampleMessage: string
    useCaseDesc: string
    monthlyVolume: number
    privacyPolicyUrl: string
    termsUrl: string
  }) => {
    this.setUseCase(id)
    this.setSampleMessage(sampleMessage)
    this.setUseCaseDesc(useCaseDesc)
    this.setMessageVolume(monthlyVolume)
    this.setPrivacyPolicyUrl(privacyPolicyUrl)
    this.setTermsUrl(termsUrl)
  }

  setPrivacyPolicyUrl = (url: string) => {
    this.privacyPolicyUrl = url
    if (this.errorPrivacyPolicyUrl) this.errorPrivacyPolicyUrl = ''
  }

  setTermsUrl = (url: string) => {
    this.termsUrl = url
    if (this.errorTermsUrl) this.errorTermsUrl = ''
  }

  setErrorPrivacyPolicyUrl = (message: string) => {
    this.errorPrivacyPolicyUrl = message
  }

  setErrorTermsUrl = (message: string) => {
    this.errorTermsUrl = message
  }

  openUseCaseExampleModal = () => {
    const id = 'useCaseExampleModal'
    const modalContentExample =
      (this._useCaseValue !== null && USE_CASE_DESCRIPTION[this._useCaseValue]?.content) ||
      useCaseDescExampleMock

    modalStore.addModal({
      id,
      width: 540,
      title: 'Use case description example',
      ModalContent: () => (
        <ExampleModalContent
          activeUseCase={this.activeUseCaseTitle || ''}
          example={modalContentExample}
        />
      ),
      primaryAction: {
        text: 'Done',
        onAction: () => modalStore.closeModal(id),
      },
      showCloseButton: false,
    })
  }

  generateTollFreeAIDesc = async () => {
    const data = await this.aiGenerationStore.generateTollFreeAIDesc()

    data && this.setUseCaseDesc(data.result_message)
  }

  generateTollFreeAIMessage = async () => {
    const data = await this.aiGenerationStore.generateTollFreeAIMessage({
      useCaseDesc: this.useCaseDesc,
      useCaseValue: this.activeUseCase?.label || '',
    })

    data && this.setSampleMessage(data.result_message)
  }

  openSampleMessageModal = () => {
    const id = 'sampleMessageModal'
    const modalContentExample =
      (this._useCaseValue !== null && USE_CASE_SAMPLE_MESSAGE[this._useCaseValue]?.content) ||
      useSampleMessageExampleMock

    modalStore.addModal({
      id,
      width: 540,
      title: 'Sample message example',
      hiddenContent: true,
      ModalContent: () => (
        <ExampleModalContent
          activeUseCase={this.activeUseCaseTitle || ''}
          withMustInclude
          example={modalContentExample}
        />
      ),
      primaryAction: {
        text: 'Done',
        onAction: () => modalStore.closeModal(id),
      },
      showCloseButton: false,
    })
  }

  checkUrls = async () => {
    return await checkComplianceUrls({
      privacyPolicyUrl: {
        value: this.privacyPolicyUrl,
        setValue: this.setPrivacyPolicyUrl,
        setError: this.setErrorPrivacyPolicyUrl,
      },
      termsUrl: {
        value: this.termsUrl,
        setValue: this.setTermsUrl,
        setError: this.setErrorTermsUrl,
      },
    })
  }

  nextStepClick = async () => {
    const { profileId } = this.tollFreeStepsStore
    const { activeUseCaseNumber } = this.tollFreeStepsStore.useCaseDetailsStore

    const id = activeUseCaseNumber ? activeUseCaseNumber.individualTollFreeProfile?.id : profileId

    if (!id || !this.useCaseValue) return

    this.tollFreeStepsStore.setStepLoading(true)
    const isUrlsValid = await this.checkUrls()

    if (!isUrlsValid) {
      this.tollFreeStepsStore.setStepLoading(false)
      return
    }

    try {
      const { data } = await ComplianceApi.updateTollFreeUseCase(
        {
          monthly_volume: String(this.messageVolumeValue),
          use_case_id: this.useCaseValue,
          sample_message_1: this.sampleMessage,
          use_case_description: this.useCaseDesc,
          privacy_policy_url: this.privacyPolicyUrl,
          terms_url: this.termsUrl,
        },
        id
      )

      if (data) {
        uiStore.changeRoute({
          path: this.tollFreeStepsStore.tollFreeNavigationStore.getNextUrl,
        })

        this.tollFreeStepsStore.setDataFromTollFreeRequest(data)
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this.tollFreeStepsStore.setStepLoading(false)
    }
  }
}
